import { FC } from 'react';
import { FunctionField, ReferenceManyField, TextField } from 'react-admin';

import {
  GetB2bRecurringInvestmentAllocationOneResponse,
  GetB2bRecurringInvestmentAllocationsQuery,
  getB2bRecurringInvestmentAllocationsSortColumns,
} from '@Common/types/bo/b2b/recurring_investments';

import { b2bRecurringInvestmentAllocationSourceChoices } from '@sh4res/bo-common/choices/recurring_investments';
import { DatagridTyped, Pagination } from '@sh4res/bo-common/components';
import { CurrencyField, IdField, SelectChipField, UTCDateField } from '@sh4res/bo-common/fields';
import { useResourceBasePath, useSelectedColumns } from '@sh4res/bo-common/hooks';
import { ColumnList, RaSort } from '@sh4res/bo-common/utils';

const defaultSort: RaSort<GetB2bRecurringInvestmentAllocationsQuery> = {
  field: 'created_at',
  order: 'DESC',
};

const defaultColumns: (keyof GetB2bRecurringInvestmentAllocationOneResponse)[] = [
  'id',
  'instrument_symbol',
  'instrument_name',
  'allocation_percentage',
  'allocation_amount',
  'source',
  'b2b_fully_managed_allocation_template_id',
];

export const B2bRecurringInvestmentAllocationsList: FC = () => {
  const resourceBasePath = useResourceBasePath();
  const displayedColumns = useSelectedColumns<GetB2bRecurringInvestmentAllocationOneResponse>({
    preferences: 'b2b.recurring_investments.allocations.list.columns',
    defaultColumns,
  });

  const columns: ColumnList<GetB2bRecurringInvestmentAllocationOneResponse> = {
    id: (
      <IdField
        data-testid="BoExternalB2bRecurringInvestmentAllocationsList_IdField"
        reference="instruments"
      />
    ),
    b2b_recurring_investment_id: (
      <IdField
        data-testid="BoExternalB2bRecurringInvestmentAllocationsList_RecurringInvestmentIdField"
        reference={`${resourceBasePath}recurring_investments`}
      />
    ),
    source: (
      <SelectChipField
        data-testid="BoExternalB2bRecurringInvestmentAllocationsList_SourceField"
        choices={b2bRecurringInvestmentAllocationSourceChoices}
      />
    ),
    instrument_symbol: (
      <TextField data-testid="BoExternalB2bRecurringInvestmentAllocationsList_InstrumentSymbolField" />
    ),
    instrument_name: (
      <TextField data-testid="BoExternalB2bRecurringInvestmentAllocationsList_InstrumentNameField" />
    ),
    allocation_percentage: (
      <FunctionField
        data-testid="BoExternalB2bRecurringInvestmentAllocationsList_AllocationPercentageField"
        render={({
          allocation_percentage: allocationPercentage,
        }: GetB2bRecurringInvestmentAllocationOneResponse) => `${allocationPercentage}%`}
      />
    ),
    allocation_amount: (
      <CurrencyField
        testId="BoExternalB2bRecurringInvestmentAllocationsList_AllocationAmountField"
        currency="user_currency"
      />
    ),
    user_currency: (
      <TextField data-testid="BoExternalB2bRecurringInvestmentAllocationsList_UserCurrencyField" />
    ),
    b2b_fully_managed_allocation_id: (
      // eslint-disable-next-line @sh4res/jsx-id-field-for-resources -- managed grids have no BO resource to link to
      <TextField data-testid="BoExternalB2bRecurringInvestmentAllocationsList_FullyManagedAllocationIdField" />
    ),
    b2b_fully_managed_allocation_template_id: (
      <IdField
        data-testid="BoExternalB2bRecurringInvestmentAllocationsList_FullyManagedAllocationTemplateIdField"
        reference={'b2b/investor_profile'}
      />
    ),
    created_at: (
      <UTCDateField
        data-testid="BoExternalB2bRecurringInvestmentAllocationsList_CreatedAtField"
        showTime
      />
    ),
    updated_at: (
      <UTCDateField
        data-testid="BoExternalB2bRecurringInvestmentAllocationsList_UpdatedAtField"
        showTime
      />
    ),
  };

  return (
    <ReferenceManyField
      data-testid="BoExternalB2bRecurringInvestmentAllocationsList_ReferenceManyField"
      reference={`${resourceBasePath}recurring_investments/allocations`}
      target="b2b_recurring_investment_id"
      sort={defaultSort}
      perPage={10}
      pagination={<Pagination />}>
      <DatagridTyped<GetB2bRecurringInvestmentAllocationOneResponse>
        sortableColumns={getB2bRecurringInvestmentAllocationsSortColumns}
        columnRegistry={columns}
        displayedColumns={displayedColumns}
        bulkActionButtons={false}
      />
    </ReferenceManyField>
  );
};
